/**
 * Локальное хранилище (IndexedDB).
 *
 * Подключается и основным приложением, и Service Worker'ом — поэтому здесь нет
 * ни libsodium, ни Preact, ничего тяжёлого.
 *
 * О хранении ключей начистоту: ключи чатов и приватный ключ личности лежат
 * здесь в открытом виде. Иначе никак — Service Worker должен расшифровать
 * входящий пуш без участия пользователя, а значит ключ обязан быть доступен
 * без ввода пароля. Защита тут — изоляция по origin в браузере и блокировка
 * самого устройства. На чужом или незапертом устройстве переписка читается;
 * для такого случая есть выход из аккаунта, который стирает всё локальное.
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { PublicUser, WireChat, WireMessage } from './types.js';

export interface StoredChat extends WireChat {
  /** Расшифрованное название группы. Сервер видит только шифротекст. */
  title: string | null;
  /** Последнее сообщение открытым текстом — для списка чатов. */
  preview: string | null;
  previewAt: number;
}

export interface StoredMessage extends WireMessage {
  /** null означает, что расшифровать не вышло: нет ключа этой эпохи. */
  plaintext: string | null;
}

export interface OutboxItem {
  localId: string;
  chatId: string;
  keyEpoch: number;
  nonce: string;
  ciphertext: string;
  plaintext: string;
  createdAt: number;
  attempts: number;
}

/** Расшифрованная аватарка в кеше. */
export interface CachedAvatar {
  userId: string;
  dataUrl: string;
  updatedAt: number;
}

interface MessSchema extends DBSchema {
  meta: { key: string; value: unknown };
  chatKeys: { key: string; value: { chatId: string; epoch: number; key: Uint8Array } };
  chats: { key: string; value: StoredChat };
  messages: {
    key: string;
    value: StoredMessage;
    indexes: { byChatSeq: [string, number] };
  };
  outbox: { key: string; value: OutboxItem; indexes: { byChat: string } };
  users: { key: string; value: PublicUser };
  avatars: { key: string; value: CachedAvatar };
}

let dbPromise: Promise<IDBPDatabase<MessSchema>> | null = null;

export function database(): Promise<IDBPDatabase<MessSchema>> {
  if (!dbPromise) {
    // Версия 2 добавляет хранилище аватарок. Обновление идёт по возрастанию
    // без else, чтобы база любой прежней версии догнала актуальную за один
    // проход — у пользователей, которые уже переписывались, база версии 1.
    dbPromise = openDB<MessSchema>('mess', 2, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          db.createObjectStore('meta');
          db.createObjectStore('chatKeys');
          db.createObjectStore('chats', { keyPath: 'id' });

          const messages = db.createObjectStore('messages', { keyPath: 'id' });
          messages.createIndex('byChatSeq', ['chatId', 'seq']);

          const outbox = db.createObjectStore('outbox', { keyPath: 'localId' });
          outbox.createIndex('byChat', 'chatId');

          db.createObjectStore('users', { keyPath: 'id' });
        }
        if (oldVersion < 2) {
          db.createObjectStore('avatars', { keyPath: 'userId' });
        }
      },
    });
  }
  return dbPromise;
}

// ─── Сессия ──────────────────────────────────────────────────────────────────

export interface Session {
  token: string;
  userId: string;
  username: string;
  displayName: string;
  identityPublicKey: string;
  /** Приватный ключ X25519 в открытом виде — см. оговорку в шапке файла. */
  identitySecret: Uint8Array;
}

export async function saveSession(session: Session): Promise<void> {
  const db = await database();
  await db.put('meta', session, 'session');
}

export async function loadSession(): Promise<Session | null> {
  const db = await database();
  return ((await db.get('meta', 'session')) as Session | undefined) ?? null;
}

/** Выход: стирает всё локальное, включая ключи. После этого история не читается. */
export async function clearEverything(): Promise<void> {
  const db = await database();
  const stores = ['meta', 'chatKeys', 'chats', 'messages', 'outbox', 'users', 'avatars'] as const;
  const tx = db.transaction(stores, 'readwrite');
  await Promise.all([...stores.map((name) => tx.objectStore(name).clear()), tx.done]);
}

// ─── Ключи чатов ─────────────────────────────────────────────────────────────

const chatKeyId = (chatId: string, epoch: number): string => `${chatId}:${epoch}`;

export async function putChatKey(chatId: string, epoch: number, key: Uint8Array): Promise<void> {
  const db = await database();
  await db.put('chatKeys', { chatId, epoch, key }, chatKeyId(chatId, epoch));
}

export async function getChatKey(chatId: string, epoch: number): Promise<Uint8Array | null> {
  const db = await database();
  const row = await db.get('chatKeys', chatKeyId(chatId, epoch));
  return row?.key ?? null;
}

// ─── Чаты, сообщения, участники ──────────────────────────────────────────────

export async function putChat(chat: StoredChat): Promise<void> {
  const db = await database();
  await db.put('chats', chat);
}

export async function getChat(chatId: string): Promise<StoredChat | null> {
  const db = await database();
  return (await db.get('chats', chatId)) ?? null;
}

export async function allChats(): Promise<StoredChat[]> {
  const db = await database();
  const chats = await db.getAll('chats');
  return chats.sort((a, b) => b.previewAt - a.previewAt);
}

/**
 * Стирает чат с устройства целиком: сам чат, сообщения, очередь отправки
 * и ключи всех эпох.
 *
 * Ключи удаляются намеренно, хотя без них не прочитать историю: сервер
 * выдаст их заново при следующей синхронизации, а на устройстве после
 * удаления не должно оставаться ничего от этого чата.
 */
export async function deleteChat(chatId: string): Promise<void> {
  const db = await database();
  const tx = db.transaction(['chats', 'messages', 'outbox', 'chatKeys'], 'readwrite');
  await tx.objectStore('chats').delete(chatId);

  const index = tx.objectStore('messages').index('byChatSeq');
  for await (const cursor of index.iterate(
    IDBKeyRange.bound([chatId, 0], [chatId, Number.MAX_SAFE_INTEGER]),
  )) {
    await cursor.delete();
  }

  for await (const cursor of tx.objectStore('outbox').index('byChat').iterate(chatId)) {
    await cursor.delete();
  }

  // Ключи лежат под составным идентификатором «чат:эпоха», отдельного индекса
  // по чату нет — перебираем и сверяем поле, эпох у чата единицы.
  for await (const cursor of tx.objectStore('chatKeys').iterate()) {
    if (cursor.value.chatId === chatId) await cursor.delete();
  }

  await tx.done;
}

export async function putMessages(messages: StoredMessage[]): Promise<void> {
  if (messages.length === 0) return;
  const db = await database();
  const tx = db.transaction('messages', 'readwrite');
  await Promise.all([...messages.map((message) => tx.store.put(message)), tx.done]);
}

export async function chatMessages(chatId: string, limit = 200): Promise<StoredMessage[]> {
  const db = await database();
  const all = await db.getAllFromIndex(
    'messages',
    'byChatSeq',
    IDBKeyRange.bound([chatId, 0], [chatId, Number.MAX_SAFE_INTEGER]),
  );
  return all.slice(-limit);
}

export async function highestSeq(chatId: string): Promise<number> {
  const messages = await chatMessages(chatId, 1);
  return messages.at(-1)?.seq ?? 0;
}

export async function putUsers(users: PublicUser[]): Promise<void> {
  if (users.length === 0) return;
  const db = await database();
  const tx = db.transaction('users', 'readwrite');
  await Promise.all([...users.map((user) => tx.store.put(user)), tx.done]);
}

export async function getUser(userId: string): Promise<PublicUser | null> {
  const db = await database();
  return (await db.get('users', userId)) ?? null;
}

export async function allUsers(): Promise<PublicUser[]> {
  const db = await database();
  return db.getAll('users');
}

// ─── Аватарки ────────────────────────────────────────────────────────────────

export async function putAvatar(avatar: CachedAvatar): Promise<void> {
  const db = await database();
  await db.put('avatars', avatar);
}

export async function getAvatar(userId: string): Promise<CachedAvatar | null> {
  const db = await database();
  return (await db.get('avatars', userId)) ?? null;
}

export async function allAvatars(): Promise<CachedAvatar[]> {
  const db = await database();
  return db.getAll('avatars');
}

export async function deleteAvatar(userId: string): Promise<void> {
  const db = await database();
  await db.delete('avatars', userId);
}

/**
 * Ключ собственной аватарки.
 *
 * Хранится отдельно, чтобы допечатать его новым собеседникам, не заставляя
 * пользователя заново загружать картинку.
 */
export async function saveOwnAvatarKey(key: Uint8Array): Promise<void> {
  const db = await database();
  await db.put('meta', key, 'avatarKey');
}

export async function loadOwnAvatarKey(): Promise<Uint8Array | null> {
  const db = await database();
  const value = (await db.get('meta', 'avatarKey')) as Uint8Array | undefined;
  return value ?? null;
}

// ─── Очередь исходящих ───────────────────────────────────────────────────────

export async function enqueue(item: OutboxItem): Promise<void> {
  const db = await database();
  await db.put('outbox', item);
}

export async function dequeue(localId: string): Promise<void> {
  const db = await database();
  await db.delete('outbox', localId);
}

export async function pendingOutbox(): Promise<OutboxItem[]> {
  const db = await database();
  const items = await db.getAll('outbox');
  return items.sort((a, b) => a.createdAt - b.createdAt);
}

export async function bumpAttempts(localId: string): Promise<void> {
  const db = await database();
  const item = await db.get('outbox', localId);
  if (!item) return;
  await db.put('outbox', { ...item, attempts: item.attempts + 1 });
}
